import type { ReactNode } from 'react'
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router'
import { LogOut, Settings, UserPlus } from 'lucide-react'
import { useAlerts } from '../lib/adminQueries'
import { BOUTIQUE_LOCALE } from '../lib/boutique'
import { useStaffToken } from '../lib/staffToken'
import { uiStrings } from '../lib/strings'
import { MarketerNotHere, MarketingVisitsPage } from './admin/MarketingVisitsPage'
import { hasSubNav, isAt, landingOf, sectionAt, sectionsFor, tabCurrent } from './consoleNav'

/**
 * The console's frame, once somebody is through the gate.
 *
 * The gate itself is no longer here. It used to be `StaffGate`, a token field
 * rendered in place of the console whenever localStorage was empty — which
 * meant the console and the sign-in page were the same URL, and there was no
 * address anyone could be sent to in order to sign in. It is now
 * `/staff` (`StaffSignInRoute`), and this redirects to it.
 *
 * That redirect is `<Navigate replace>` rather than a render, so the browser's
 * back button works: a stylist who signs in and then presses back should end up
 * where she came from, not on the gate she has just passed.
 */

/**
 * The owner's pages, at their own addresses.
 *
 * Hiding a tab is not a gate: `/admin/sync` is a URL, and a bookmark, a browser
 * autocomplete or a link a colleague pasted will all reach it. Without this the
 * page renders for a stylist and then fills with 403s, which reads as an outage
 * rather than as an answer.
 *
 * It says which role and does NOT offer a way out, because there isn't one she
 * can take: signing out and back in as herself lands her here again. "Ask an
 * owner" is the whole answer, and it is the same one the API's 403 gives.
 *
 * `role === 'owner'` rather than the contract's `atLeast`, here and in
 * `sectionsFor` — that function's note says why.
 */
export function OwnerOnly({ children }: { children: ReactNode }) {
  const { role } = useStaffToken()
  if (role === 'owner') return <>{children}</>
  return (
    <section className="st-card">
      <h1 className="st-display text-lg">This one is the owner&apos;s</h1>
      <p className="st-meta mt-2">
        The payment report, commission, syncing gowns, publishing to the website and the shop&apos;s settings are the
        owner&apos;s to run. Ask them to sign in here, or send them this page.
      </p>
      <Link to="/admin" className="st-btn-quiet mt-4 inline-flex">
        Back to appointments
      </Link>
    </section>
  )
}

export function AdminLayout() {
  const { token, identity, role, source, clearToken } = useStaffToken()
  // A marketer may not read the alerts; asking would only collect a 403.
  const alerts = useAlerts(role === 'marketer' ? null : token).data
  const { pathname } = useLocation()

  if (!token) return <Navigate to="/staff" replace />
  if (role === 'marketer') {
    return <MarketerConsole name={identity?.displayName ?? identity?.email ?? ''} pathname={pathname} onSignOut={clearToken} />
  }

  const sections = sectionsFor(role)
  const current = sectionAt(sections, pathname)
  const tabs = sections.filter((section) => section.placement === 'tab')
  const settings = sections.find((section) => section.placement === 'gear')
  const subNav = current && hasSubNav(current) ? current : undefined

  return (
    <div className="st-root">
      <header className="print-hide border-b" style={{ borderColor: 'var(--st-rule)', background: 'var(--st-surface)' }}>
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 pt-4">
          <span className="st-display text-lg">Fitting Room</span>
          <span className="st-eyebrow hidden sm:inline">Staff console</span>

          {/*
            The guest entry, from the console.

            This is the actual use case that was previously served only by the
            dev index: a bride is standing at the desk with no booking, and the
            stylist needs a tablet already on the door screen. She should not
            have to know a URL, and she certainly should not have to go via a
            page that calls itself scaffolding.

            A plain link rather than a button that mints a session here: the
            session belongs to the customer's browser, and the stylist is about
            to hand her the device. Minting it in the console's tab would put
            the bride inside the staff console's history.
          */}
          <Link to="/start" className="st-btn-quiet ml-auto" title="Open the walk-in entry to hand to a customer">
            <UserPlus size={14} aria-hidden />
            Start a walk-in
          </Link>

          {/*
            Who is signed in, and as what. The role is drawn because a stylist
            who cannot press "Apply this sync" should be able to see WHY without
            pressing it — the button being absent with no explanation reads as a
            bug.
          */}
          <span className="st-meta hidden md:inline" title={source === 'shared-token' ? 'Signed in with the shared boutique token' : undefined}>
            {identity?.displayName ?? identity?.email ?? 'Shared token'}
            {role ? ` · ${role}` : ''}
          </span>

          {/*
            Settings as a gear beside Sign out rather than a tab: the team, the
            back office's numbers, the size chart and the theme are set once and
            revisited rarely, and a tab spent on them is a tab every eye has to
            skip all day. Owner-only, like every page behind it, so a stylist
            has no gear at all. It is lit the way a tab is — the stylesheet
            hangs the gold off the same `aria-current`.
          */}
          {settings ? (
            <Link
              to={landingOf(settings)}
              className="st-iconbtn shrink-0"
              aria-label={settings.label}
              title={settings.label}
              aria-current={tabCurrent(settings, current)}
            >
              <Settings size={15} aria-hidden />
            </Link>
          ) : null}

          {/* Just the icon on a phone, so an owner's row — the walk-in, the
              gear and this — fits 375px on one line. The walk-in keeps its
              words: it is the one pressed with a customer waiting. */}
          <button type="button" className="st-btn-quiet shrink-0" onClick={clearToken} aria-label="Sign out" title="Sign out">
            <LogOut size={14} aria-hidden />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
        {/* The tab rule and the header rule are the same line: the active tab's
            gold sits ON it, which is what makes it read as a position rather
            than as a decoration floating nearby. */}
        {/* Scrolls sideways on a phone rather than pushing the page wider. The
            tabs outgrew one line at 375px before they were grouped into
            sections, and five of them with a count on one is still close.
            `pb-px` keeps the active tab's gold inside the scroller, on the
            rule. */}
        <nav aria-label="Console" className="mx-auto -mb-px flex max-w-6xl gap-6 overflow-x-auto px-5 pb-px">
          {/* A tab is lit for every page in its section, not only the one it
              links to, so it is a `Link` carrying the section's `aria-current`
              rather than a `NavLink`, which knows only its own address. The
              stylesheet hangs the gold off `aria-current` either way — so the
              underline and the announced position can never disagree. */}
          {tabs.map((section) => (
            <Link
              key={section.id}
              to={landingOf(section)}
              aria-current={tabCurrent(section, current)}
              className="st-tab shrink-0 whitespace-nowrap"
            >
              {section.label}
              {/* The count rides on To do, the tab Alerts lives under, so it is
                  seen from every screen; red only when something threatens a
                  wedding. It is the alerts' own total with nothing added: a
                  due follow-up is already one of the alerts (docs/22), and
                  inquiries are not counted at all. */}
              {section.id === 'to-do' && alerts && alerts.total > 0 ? (
                <span
                  className="st-nav-count"
                  data-severity={alerts.critical > 0 ? 'critical' : 'warning'}
                  aria-label={`${alerts.total} ${alerts.total === 1 ? 'alert' : 'alerts'}${alerts.critical > 0 ? `, ${alerts.critical} critical` : ''}`}
                >
                  {alerts.total}
                </span>
              ) : null}
            </Link>
          ))}
        </nav>
      </header>
      {/* The section's pages, under the rule, when it has more than one this
          person can open. Quieter than the tabs — smaller, and no gold —
          because the gold has already said where they are; this row only says
          which of the section's pages. Here `NavLink` is right: each entry is
          one address. */}
      {subNav ? (
        <nav aria-label={subNav.label} className="print-hide mx-auto flex max-w-6xl gap-5 overflow-x-auto px-5 pt-4">
          {subNav.pages.map((page) => (
            <NavLink key={page.to} to={page.to} end={page.end} className="st-subtab shrink-0 whitespace-nowrap">
              {page.label}
            </NavLink>
          ))}
        </nav>
      ) : null}
      <main className={`mx-auto max-w-6xl px-5 ${subNav ? 'pt-5 pb-7' : 'py-7'}`}>
        <Outlet />
      </main>
    </div>
  )
}

/**
 * The console a marketing partner signs in to: one tab, Appointments, and
 * nothing else — not the walk-in, not the gear, not the staff pages under it.
 *
 * It does not render the `<Outlet />` at all. Every staff page under `/admin`
 * would ask the API for something a marketer is refused (the server is the
 * gate; `MARKETER_ALLOWED` in `packages/contracts`), so a pasted link to one
 * gets `MarketerNotHere` instead of a page full of 403s.
 */
function MarketerConsole({ name, pathname, onSignOut }: { readonly name: string; readonly pathname: string; readonly onSignOut: () => void }) {
  const t = uiStrings(BOUTIQUE_LOCALE)
  const atList = isAt(pathname, '/admin', true)
  return (
    <div className="st-root">
      <header className="print-hide border-b" style={{ borderColor: 'var(--st-rule)', background: 'var(--st-surface)' }}>
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 pt-4">
          <span className="st-display text-lg">Fitting Room</span>
          <span className="st-meta ml-auto hidden md:inline">
            {name ? `${name} · ` : ''}
            {t.marketerRole}
          </span>
          <button type="button" className="st-btn-quiet shrink-0 md:ml-0 ml-auto" onClick={onSignOut} aria-label="Sign out" title="Sign out">
            <LogOut size={14} aria-hidden />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
        <nav aria-label="Console" className="mx-auto -mb-px flex max-w-6xl gap-6 overflow-x-auto px-5 pb-px">
          <Link to="/admin" aria-current={atList ? 'page' : undefined} className="st-tab shrink-0 whitespace-nowrap">
            {t.marketingTitle}
          </Link>
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-7">{atList ? <MarketingVisitsPage /> : <MarketerNotHere />}</main>
    </div>
  )
}
